import type { BarcodeScanner, ScannerSettings} from '../barcode-scanner.types';
import {BarcodeFormat} from '../barcode-scanner.types';
import type { Subscription} from 'rxjs';
import {Subject} from 'rxjs';
import type { Exception, Result} from '@zxing/library/';
import {BrowserBarcodeReader, DecodeHintType} from '@zxing/library/';
import {Barcode} from '../barcode';
import {BarcodeFormatMapper} from '../barcode-format-mapper';
import bind from 'bind-decorator';


export class ZxingJsBarcodeScanner implements BarcodeScanner {

    private barcodeScanner;
    private formatMapper: BarcodeFormatMapper;
    private isScannerStarted = false
    private result$: Subject<Barcode> = new Subject<Barcode>();
    private subscription: Subscription;


    constructor(private readonly videoSourceElement: HTMLVideoElement,
                private readonly scanSettings: ScannerSettings) {

        this.formatMapper = new BarcodeFormatMapper();
        const hints = this.constructDecodeHints(scanSettings);

        this.barcodeScanner = new BrowserBarcodeReader(scanSettings.timeBetweenScansInMillis, hints);
        this.barcodeScanner.reset();
    }

    public async startScanner(onResult: (result: Barcode) => void): Promise<void> {

        if (this.isStarted()) {
            return Promise.reject(new Error('Already started'))
        }

        this.isScannerStarted = true;
        this.subscription = this.result$.subscribe(onResult)

        await this.barcodeScanner.decodeFromVideoDevice(
            undefined,
            this.videoSourceElement,
            this.processResult);
    }

    public stopScanner(): void {

        if (!this.isStarted()) {
            return;
        }

        this.subscription.unsubscribe();

        this.barcodeScanner.stopAsyncDecode();
        this.barcodeScanner.stopContinuousDecode();
        this.barcodeScanner.reset();

        this.isScannerStarted = false;
    }

    public isStarted(): boolean {
        return this.isScannerStarted;
    }

    private constructDecodeHints(scanSettings: ScannerSettings) {

        const hints = new Map();

        hints.set(DecodeHintType.TRY_HARDER, scanSettings.tryHarder);
        hints.set(DecodeHintType.PURE_BARCODE, true);

        if (scanSettings.codeFormats?.length > 0) {
            const formats = scanSettings.codeFormats.map(this.formatMapper.mapFromInternal);
            hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
        }

        return hints;
    }

    @bind
    private processResult(result: Result, error: Exception) {

        let barcode;
        if (result) {
            barcode = new Barcode(result.getText(), this.formatMapper.mapToInternal(result.getBarcodeFormat()), '');
        } else {
            barcode = new Barcode('', BarcodeFormat.ANY, error.name);
        }
        this.result$.next(barcode);
    }
}